import React, { useEffect, useState } from 'react';
import { Breadcrumb, Layout, theme, Row, Col, Typography, Table, Card, Tag, Space, Button, Upload} from 'antd';
import Headerdiv from "../../layout/header";
import Footerdiv from "../../layout/footer";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Link, useNavigate } from "react-router-dom";
import { MAINAPIURL, APPBACKGROUND_COLOR } from '../../appsettings.js';
import { DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import { AlertError, AlertSuccess } from '../../usableclasses.js';

ChartJS.register(ArcElement, Tooltip, Legend);
const { Content } = Layout;
const { Title, Text } = Typography;


function ProductExcelUpload() {
    
    const ApiURL = MAINAPIURL;
    const TOKEN = localStorage.getItem("AUTHCODE");
    const UPDATED_BY = localStorage.getItem("USERID");
    const { token: { colorBgContainer, borderRadiusLG }, } = theme.useToken();
    const navigate = useNavigate();
    const [priceChangeLoading, setPriceChangeLoading] = useState(false);
    const [fileData, setFileData] = useState([]);
    const [validationErrors, setValidationErrors] = useState({});
    const [uploadLoading, setUploadLoading] = useState(false);
    const [saveLoading, setSaveLoading] = useState(false);
 
  

    const handleDownloadPriceChangeExcel = async () => {
        try {
            setPriceChangeLoading(true);
            const response = await fetch(`${ApiURL}/products/excelforpricechange`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${TOKEN}`,
                    'Content-Type': 'application/json',
                    'SystemAccessToken': TOKEN
                },
            });
            
            if (!response.ok) {
                throw new Error('Failed to download price change excel');
            }
            
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'product-price-change.xlsx';
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (error) {
            console.error('Error downloading price change excel:', error);
        } finally {
            setPriceChangeLoading(false);
        }
    };

    const validateRow = (record) => {
        const errors = {};
        
        // Required fields validation - match exact Excel header names
        const requiredFields = ['Code', 'UseAs', 'CostPrice', 'OfferPrice', 'StandardPrice'];
        
        requiredFields.forEach(field => {
            if (!record[field] && record[field] !== 0) {
                errors[field] = true;
            }
        });
        
        // Price fields should be numbers
        const priceFields = ['CostPrice', 'OfferPrice', 'StandardPrice'];
        priceFields.forEach(field => {
            if (record[field] && isNaN(Number(record[field]))) {
                errors[field] = true;
            }
        });
        
        // Ensure ProSku is a valid string
        if (record.ProSku !== undefined && (typeof record.ProSku !== 'string' && typeof record.ProSku !== 'number')) {
            errors.ProSku = true;
        }
        
        return errors;
    };

    const processExcelFile = (file) => {
        setUploadLoading(true);
        const reader = new FileReader();
        
        reader.onload = (e) => {
            try {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: 'array' });
                const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
                const jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 2 });
                
                // Process validation
                const errors = {};
                jsonData.forEach((row, index) => {
                    const rowErrors = validateRow(row);
                    if (Object.keys(rowErrors).length > 0) {
                        errors[index] = rowErrors;
                    }
                });
                
                setFileData(jsonData);
                setValidationErrors(errors);
                setUploadLoading(false);
            } catch (error) {
                console.error('Error processing file:', error);
                AlertError('Failed to process the Excel file');
                setUploadLoading(false);
            }
        };
        
        reader.onerror = () => {
            AlertError('Failed to read the file');
            setUploadLoading(false);
        };
        
        reader.readAsArrayBuffer(file);
        
        // Return false to prevent default upload behavior
        return false;
    };

    const handleFileUpload = () => {
        const upload = document.createElement('input');
        upload.type = 'file';
        upload.accept = '.xlsx, .xls';
        upload.onchange = (e) => {
            if (e.target.files.length > 0) {
                const file = e.target.files[0];
                processExcelFile(file);
            }
        };
        upload.click();
    };

    const handleUploadPriceChanges = async () => {

        if (Object.keys(validationErrors).length > 0) {
            AlertError('Please fix validation errors before uploading');
            return;
        }

    
        const formattedData = fileData.map((item, index) => {
  
            const code = item.Code ? String(item.Code).trim() : '';
            const useAs = item.UseAs ? String(item.UseAs).trim() : '';
            const costPrice = item.CostPrice ? Number(item.CostPrice) : 0;
            const offerPrice = item.OfferPrice ? Number(item.OfferPrice) : 0;
            const standardPrice = item.StandardPrice ? Number(item.StandardPrice) : 0;
            
            return {
                code,
                useAs,
                costPrice,
                offerPrice,
                standardPrice
            };
        });

        setSaveLoading(true);
        try {
           
            
            const response = await fetch(`${ApiURL}/products/pricelistupdatebyexcel`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${TOKEN}`,
                    'Content-Type': 'application/json',
                    'SystemAccessToken': TOKEN
                },
                body: JSON.stringify(formattedData)
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => null);
                console.error('API Error Response:', errorData);
                throw new Error(`Failed to upload price changes: ${response.status} ${response.statusText}`);
            }


            AlertSuccess('Price changes uploaded successfully');
            navigate('/master/productlist');
            setFileData([]);
            setValidationErrors({});
        } catch (error) {
            console.error('Error uploading price changes:', error);
            AlertError(`Failed to upload price changes: ${error.message}`);
        } finally {
            setSaveLoading(false);
        }
    };

    const columns = [
        {
            title: 'Code',
            dataIndex: 'Code',
            key: 'Code',
            render: (text, record, index) => {
                const hasError = validationErrors[index]?.ProSku;
                return (
                    <div>
                        <span style={{ 
                            color: hasError ? '#ff4d4f' : 'inherit',
                            backgroundColor: hasError ? '#fff2f0' : 'transparent',
                            padding: hasError ? '2px 5px' : '0',
                            border: hasError ? '1px solid #ffccc7' : 'none',
                            borderRadius: '2px',
                            display: 'inline-block'
                        }}>
                            {text || ''}
                            {hasError && <span style={{ color: '#ff4d4f', marginLeft: '5px' }}>(Missing)</span>}
                        </span>
                    </div>
                );
            }
        },
        {
            title: 'Name',
            dataIndex: 'Name',
            key: 'Name'
        },
        {
            title: 'Use As',
            dataIndex: 'UseAs',
            key: 'UseAs',
            render: (text, record, index) => {
                const hasError = validationErrors[index]?.ProUseAs;
                return (
                    <div>
                        <span style={{ 
                            color: hasError ? '#ff4d4f' : 'inherit',
                            backgroundColor: hasError ? '#fff2f0' : 'transparent',
                            padding: hasError ? '2px 5px' : '0',
                            border: hasError ? '1px solid #ffccc7' : 'none',
                            borderRadius: '2px',
                            display: 'inline-block'
                        }}>
                            {text || ''}
                            {hasError && <span style={{ color: '#ff4d4f', marginLeft: '5px' }}>(Missing)</span>}
                        </span>
                    </div>
                );
            }
        },
        {
            title: 'Category',
            dataIndex: 'Category',
            key: 'Category'
        },
        {
            title: 'Brand',
            dataIndex: 'Brand',
            key: 'Brand'
        },
        {
            title: 'Size',
            dataIndex: 'Size',
            key: 'Size'
        },
        {
            title: 'Cost Price',
            dataIndex: 'CostPrice',
            key: 'CostPrice',
            render: (text, record, index) => {
                const hasError = validationErrors[index]?.ProCostPrice;
                return (
                    <div>
                        <span style={{ 
                            color: hasError ? '#ff4d4f' : 'inherit',
                            backgroundColor: hasError ? '#fff2f0' : 'transparent',
                            padding: hasError ? '2px 5px' : '0',
                            border: hasError ? '1px solid #ffccc7' : 'none',
                            borderRadius: '2px',
                            display: 'inline-block'
                        }}>
                            {text || ''}
                            {hasError && <span style={{ color: '#ff4d4f', marginLeft: '5px' }}>(Missing)</span>}
                        </span>
                    </div>
                );
            }
        },
        {
            title: 'Offer Price',
            dataIndex: 'OfferPrice',
            key: 'OfferPrice',
            render: (text, record, index) => {
                const hasError = validationErrors[index]?.ProOfferPrice;
                return (
                    <div>
                        <span style={{ 
                            color: hasError ? '#ff4d4f' : 'inherit',
                            backgroundColor: hasError ? '#fff2f0' : 'transparent',
                            padding: hasError ? '2px 5px' : '0',
                            border: hasError ? '1px solid #ffccc7' : 'none',
                            borderRadius: '2px',
                            display: 'inline-block'
                        }}>
                            {text || ''}
                            {hasError && <span style={{ color: '#ff4d4f', marginLeft: '5px' }}>(Missing)</span>}
                        </span>
                    </div>
                );
            }
        },
        {
            title: 'Standard Price',
            dataIndex: 'StandardPrice',
            key: 'StandardPrice',
            render: (text, record, index) => {
                const hasError = validationErrors[index]?.ProStandardPrice;
                return (
                    <div>
                        <span style={{ 
                            color: hasError ? '#ff4d4f' : 'inherit',
                            backgroundColor: hasError ? '#fff2f0' : 'transparent',
                            padding: hasError ? '2px 5px' : '0',
                            border: hasError ? '1px solid #ffccc7' : 'none',
                            borderRadius: '2px',
                            display: 'inline-block'
                        }}>
                            {text || ''}
                            {hasError && <span style={{ color: '#ff4d4f', marginLeft: '5px' }}>(Missing)</span>}
                        </span>
                    </div>
                );
            }
        },
        {
            title: 'Is Sale',
            dataIndex: 'IsActiveSale',
            key: 'IsActiveSale'
        },
        {
            title: 'Is Active',
            dataIndex: 'IsActiveProduct',
            key: 'IsActiveProduct'
        }
    ];

    return (
        <Layout style={{ backgroundColor: APPBACKGROUND_COLOR }}>
            <Headerdiv />
            <Content style={{ padding: '10px', }} >
                 <Breadcrumb style={{ margin: "16px 0" }}>
                                        <Breadcrumb.Item>Master</Breadcrumb.Item>
                                        <Breadcrumb.Item><Link to={'/master/productlist'}>Products</Link></Breadcrumb.Item>
                                        <Breadcrumb.Item>Products list upload</Breadcrumb.Item>
                                    </Breadcrumb>
                <div style={{ padding: 10, minHeight: '65vh', background: colorBgContainer, borderRadius: borderRadiusLG, }} >
                  
                    
                    <Row style={{ marginTop: '20px', marginBottom: '20px' }}>
                        <Col xs={24} md={24} style={{ textAlign: 'left' }}>
                            <Space>
                                <Button 
                                    type="primary" 
                                    icon={<DownloadOutlined />} 
                                    loading={priceChangeLoading}
                                    onClick={handleDownloadPriceChangeExcel}
                                >
                                  Download Product List
                                </Button>
                                <Button 
                                    type="primary" 
                                    icon={<UploadOutlined />} 
                                    loading={uploadLoading}
                                    onClick={handleFileUpload}
                                >
                                    Upload File
                                </Button>
                            </Space>
                        </Col>
                    </Row>

                    {fileData.length > 0 && (
                        <Row style={{ marginTop: '20px' }}>
                            <Col xs={24}>
                                <Card title="Uploaded Product Data">
                                    <div style={{ marginBottom: '10px' }}>
                                        {Object.keys(validationErrors).length > 0 && (
                                            <Tag color="error">
                                                There are validation errors in the data. Fields with errors are highlighted in red.
                                            </Tag>
                                        )}
                                    </div>
                                    <Table 
                                        dataSource={fileData.map((item, index) => ({ ...item, key: index }))} 
                                        columns={columns} 
                                        pagination={{ pageSize: 10 }}
                                        scroll={{ x: 'max-content' }}
                                    />
                                    <div style={{ textAlign: 'right', marginTop: '16px' }}>
                                        <Button 
                                            type="primary" 
                                            loading={saveLoading}
                                            onClick={handleUploadPriceChanges}
                                            disabled={Object.keys(validationErrors).length > 0}
                                        >
                                            Update
                                        </Button>
                                    </div>
                                </Card>
                            </Col>
                        </Row>
                    )}
                </div>
            </Content>
            <Footerdiv />
        </Layout>
    );
}

export default ProductExcelUpload;
